import { ChevronDown, Plus, X } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Field, inputCls, Panel } from "../ui";

const LIST_FIELDS = ["features", "seo_keywords", "secondary_colors"];
const TEXT_FIELDS = ["name", "sku", "category", "short_description", "description", "material", "color", "color_hex", "style", "seo_title", "meta_description", "brand", "model", "slug"];
const NUM_FIELDS = ["height_cm", "width_cm", "depth_cm"];

export const toForm = (p) => ({
  ...Object.fromEntries(TEXT_FIELDS.map((k) => [k, p[k] ?? ""])),
  ...Object.fromEntries(LIST_FIELDS.map((k) => [k, (p[k] || []).join(", ")])),
  ...Object.fromEntries(NUM_FIELDS.map((k) => [k, p[k] != null ? String(p[k]) : ""])),
  price: String(p.price ?? ""), sale_price: p.sale_price != null ? String(p.sale_price) : "",
  min_stock: String(p.min_stock ?? 1), featured: !!p.featured, is_new: !!p.is_new,
  variants: p.variants || [], field_status: p.field_status || {},
});

export const toPayload = (f) => {
  const out = { ...Object.fromEntries(TEXT_FIELDS.map((k) => [k, f[k]])), featured: f.featured, is_new: f.is_new, variants: f.variants.filter((v) => v.label.trim()), field_status: f.field_status };
  LIST_FIELDS.forEach((k) => { out[k] = f[k].split(",").map((s) => s.trim()).filter(Boolean); });
  NUM_FIELDS.forEach((k) => { if (f[k] !== "") out[k] = Number(f[k]); });
  out.price = Number(f.price);
  out.min_stock = Number(f.min_stock || 0);
  if (f.sale_price === "") out.clear_sale_price = true; else out.sale_price = Number(f.sale_price);
  if (!out.category) delete out.category;
  if (!out.slug) delete out.slug;
  return out;
};

const Variants = ({ value, onChange }) => (
  <div className="space-y-2" data-testid="variants-editor">
    {value.map((v, i) => (
      <div key={i} className="flex items-center gap-2">
        <input data-testid={`variant-label-${i}`} value={v.label} onChange={(e) => onChange(value.map((x, k) => (k === i ? { ...x, label: e.target.value } : x)))} placeholder="Es. 100 cm" className={inputCls} />
        <label className="flex items-center gap-1.5 text-xs shrink-0"><Switch data-testid={`variant-available-${i}`} checked={v.available} onCheckedChange={(c) => onChange(value.map((x, k) => (k === i ? { ...x, available: c } : x)))} />Disp.</label>
        <button type="button" data-testid={`variant-remove-${i}`} onClick={() => onChange(value.filter((_, k) => k !== i))} className="p-2"><X className="w-4 h-4" /></button>
      </div>
    ))}
    <button type="button" data-testid="variant-add" onClick={() => onChange([...value, { label: "", available: true }])} className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-[#956842]"><Plus className="w-3.5 h-3.5" /> Aggiungi variante</button>
  </div>
);

const More = ({ title, testId, children }) => (
  <details data-testid={testId} className="group border border-[#D5B98C]/60 bg-[#FBF8F3]">
    <summary className="flex items-center justify-between px-4 py-3.5 cursor-pointer list-none text-xs uppercase tracking-widest text-[#39251D]">
      {title}<ChevronDown className="w-4 h-4 text-[#956842] transition-transform group-open:rotate-180" />
    </summary>
    <div className="px-4 pb-5 pt-1">{children}</div>
  </details>
);

export const ProductForm = ({ form, update, cats }) => {
  const st = form.field_status;
  const set = (k) => (e) => update({ [k]: e.target.value, field_status: { ...st, [k]: "verificata" } });
  const txt = (k, props = {}) => <input data-testid={`form-${k}`} value={form[k]} onChange={set(k)} className={inputCls} {...props} />;
  const area = (k, rows) => <textarea data-testid={`form-${k}`} value={form[k]} onChange={set(k)} rows={rows} className={inputCls} />;
  return (
    <div className="space-y-4" data-testid="product-form">
      <Panel title="Scheda prodotto">
        <p className="text-xs text-[#242321]/55 -mt-3 mb-5">L'AI scrive i testi: leggili, correggi se serve e premi "Approva e pubblica".</p>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2"><Field label="Nome">{txt("name")}</Field></div>
          <div className="col-span-2">
            <Field label="Categoria">
              <select data-testid="form-category" value={form.category} onChange={set("category")} className={inputCls}>
                <option value="">Seleziona…</option>{cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}{c.hidden ? " (nascosta)" : ""}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Prezzo (€)">{txt("price", { type: "number", min: 0, step: "0.01", inputMode: "decimal" })}</Field>
          <Field label="Prezzo scontato (€)" hint="Vuoto = nessuno">{txt("sale_price", { type: "number", min: 0, step: "0.01", inputMode: "decimal" })}</Field>
          <div className="col-span-2"><Field label="Descrizione breve">{area("short_description", 2)}</Field></div>
          <div className="col-span-2"><Field label="Descrizione">{area("description", 6)}</Field></div>
        </div>
      </Panel>
      <More title="Dettagli (facoltativi)" testId="form-more-details">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Materiale">{txt("material")}</Field>
          <Field label="Colore">
            <div className="flex gap-2">{txt("color")}<input type="color" data-testid="form-color_hex" value={form.color_hex || "#956842"} onChange={set("color_hex")} className="w-12 h-[42px] border border-[#D5B98C] bg-transparent" /></div>
          </Field>
          <Field label="Colori secondari" hint="Separati da virgola">{txt("secondary_colors")}</Field>
          <Field label="Stile">{txt("style")}</Field>
          <Field label="Marca">{txt("brand")}</Field>
          <Field label="Modello">{txt("model")}</Field>
          <div className="sm:col-span-2">
            <Field label="Dimensioni (cm)" hint="Misure prese a mano: l'AI non le inventa">
              <div className="grid grid-cols-3 gap-2">
                {[["height_cm", "Altezza"], ["width_cm", "Larghezza"], ["depth_cm", "Profondità"]].map(([k, l]) => <input key={k} data-testid={`form-${k}`} type="number" min="0" step="0.1" inputMode="decimal" placeholder={l} value={form[k]} onChange={set(k)} className={inputCls} />)}
              </div>
            </Field>
          </div>
          <div className="sm:col-span-2"><Field label="Caratteristiche" hint="Separate da virgola">{area("features", 2)}</Field></div>
          <div className="sm:col-span-2"><Field label="Varianti"><Variants value={form.variants} onChange={(v) => update({ variants: v })} /></Field></div>
          <Field label="SKU">{txt("sku")}</Field>
          <div />
          <label className="flex items-center justify-between text-sm border border-[#D5B98C]/60 px-3 py-2.5">In evidenza <Switch data-testid="form-featured" checked={form.featured} onCheckedChange={(c) => update({ featured: c })} /></label>
          <label className="flex items-center justify-between text-sm border border-[#D5B98C]/60 px-3 py-2.5">Novità <Switch data-testid="form-is_new" checked={form.is_new} onCheckedChange={(c) => update({ is_new: c })} /></label>
        </div>
      </More>
      <More title="SEO (compilato dall'AI)" testId="form-more-seo">
        <div className="space-y-4">
          <Field label={`Titolo SEO (${form.seo_title.length}/60)`}>{txt("seo_title")}</Field>
          <Field label={`Meta description (${form.meta_description.length}/155)`}>{area("meta_description", 3)}</Field>
          <Field label="Parole chiave" hint="Separate da virgola">{txt("seo_keywords")}</Field>
          <Field label="URL prodotto (slug)" hint="Lettere minuscole e trattini">{txt("slug")}</Field>
        </div>
      </More>
    </div>
  );
};
